import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileDown, Printer, Link2, Mail } from "lucide-react";
import { getAthlete, listTests } from "../lib/api";
import CompareChart from "../components/CompareChart";
import { BrandBanner, BrandFooter, formatLongDate } from "../components/ReportFrame";
import { speedToPace, convertSpeed } from "../lib/pace";
import EmailReportModal from "../components/EmailReportModal";
import { toast } from "sonner";

function paceToSeconds(pace) {
  if (!pace || typeof pace !== "string" || !pace.includes(":")) return null;
  const [m, s] = pace.split(":").map(Number);
  if (!Number.isFinite(m) || !Number.isFinite(s)) return null;
  return m * 60 + s;
}

function fmtSeconds(sec) {
  if (sec == null || !Number.isFinite(sec)) return null;
  const sign = sec < 0 ? "-" : "";
  const abs = Math.abs(sec);
  const m = Math.floor(abs / 60);
  const s = Math.round(abs % 60);
  return `${sign}${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Convert a stored threshold to display-unit numbers + recomputed pace.
 * Returns { hr, speed, pace, paceSec } where speed is in displayUnit and pace in min/km.
 */
function projectThreshold(th, fromUnit, displayUnit) {
  if (!th) return { hr: null, speed: null, pace: null, paceSec: null };
  const hr = th.hr != null && th.hr !== "" ? Number(th.hr) : null;
  const rawSpeed = th.speed != null && th.speed !== "" ? Number(th.speed) : null;
  const speed =
    rawSpeed == null
      ? null
      : Number(convertSpeed(rawSpeed, fromUnit || "mph", displayUnit));
  const pace = speed != null ? speedToPace(speed, displayUnit) : "";
  return { hr, speed, pace, paceSec: paceToSeconds(pace) };
}

/**
 * Render a single change row (Metric | From | To | Δ).
 */
function ChangeRow({ metric, from, to, delta, deltaText, deltaPositive }) {
  const fmt = (v) => (v == null || v === "" ? "—" : v);
  return (
    <div className="grid grid-cols-12 gap-3 items-baseline py-2.5 border-b border-[var(--tir-border)] last:border-b-0">
      <div className="col-span-3 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
        {metric}
      </div>
      <div className="col-span-3 font-mono-tir text-[var(--tir-ink)] text-base">{fmt(from)}</div>
      <div className="col-span-3 font-mono-tir text-[var(--tir-ink)] text-base">{fmt(to)}</div>
      <div
        className={`col-span-3 font-mono-tir text-sm ${
          deltaPositive == null
            ? "text-[var(--tir-ink)]"
            : deltaPositive
              ? "text-[var(--tir-burgundy)] font-semibold"
              : "text-[var(--tir-muted)]"
        }`}
      >
        {delta == null && !deltaText ? "—" : deltaText || delta}
      </div>
    </div>
  );
}

function ChangeCard({ title, subtitle, accent, oldest, newest, displayUnit }) {
  const fmtNum = (n, suffix = "", digits = 1) =>
    n == null || !Number.isFinite(n) ? null : `${n.toFixed(digits)}${suffix}`;

  // HR change (no inherent good / bad)
  const hrFrom = oldest.hr != null ? `${oldest.hr} bpm` : null;
  const hrTo = newest.hr != null ? `${newest.hr} bpm` : null;
  const hrDelta =
    oldest.hr != null && newest.hr != null
      ? `${newest.hr - oldest.hr > 0 ? "+" : ""}${newest.hr - oldest.hr} bpm`
      : null;

  // Speed change (higher = improvement)
  const speedFrom = oldest.speed != null ? `${oldest.speed.toFixed(1)} ${displayUnit}` : null;
  const speedTo = newest.speed != null ? `${newest.speed.toFixed(1)} ${displayUnit}` : null;
  const speedRaw =
    oldest.speed != null && newest.speed != null ? newest.speed - oldest.speed : null;
  const speedDeltaText =
    speedRaw == null
      ? null
      : `${speedRaw > 0 ? "+" : ""}${speedRaw.toFixed(2)} ${displayUnit} ${
          speedRaw > 0 ? "faster" : speedRaw < 0 ? "slower" : ""
        }`.trim();

  // Pace change (lower seconds = improvement)
  const paceFrom = oldest.pace || null;
  const paceTo = newest.pace || null;
  const paceDeltaSec =
    oldest.paceSec != null && newest.paceSec != null
      ? newest.paceSec - oldest.paceSec
      : null;
  const paceDeltaText =
    paceDeltaSec == null
      ? null
      : paceDeltaSec === 0
        ? "no change"
        : `${Math.abs(paceDeltaSec)} s/km ${paceDeltaSec < 0 ? "faster" : "slower"}`;

  return (
    <div
      className="border border-[var(--tir-border)] p-5"
      style={{ borderLeft: `4px solid ${accent}` }}
      data-testid={`change-card-${title.toLowerCase()}`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-serif-display text-2xl leading-none">{title}</div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
          {subtitle}
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3 pt-2 border-b border-[var(--tir-ink)] pb-1.5">
        <div className="col-span-3 text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
          Metric
        </div>
        <div className="col-span-3 text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
          Before
        </div>
        <div className="col-span-3 text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
          Now
        </div>
        <div className="col-span-3 text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
          Change
        </div>
      </div>
      <ChangeRow
        metric="HR"
        from={hrFrom}
        to={hrTo}
        delta={hrDelta}
        deltaText={hrDelta}
        deltaPositive={null}
      />
      <ChangeRow
        metric="Speed"
        from={speedFrom}
        to={speedTo}
        deltaText={speedDeltaText}
        deltaPositive={speedRaw == null ? null : speedRaw > 0}
      />
      <ChangeRow
        metric="Pace"
        from={paceFrom}
        to={paceTo}
        deltaText={paceDeltaText}
        deltaPositive={paceDeltaSec == null ? null : paceDeltaSec < 0}
      />
    </div>
  );
}

export default function CompareReportPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [athlete, setAthlete] = useState(null);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const pageRef = useRef(null);

  const requestedIds = useMemo(
    () => (params.get("tests") || "").split(",").map((s) => s.trim()).filter(Boolean),
    [params]
  );

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([getAthlete(id), listTests(id)]);
        setAthlete(a);
        setAllTests(t);
      } catch {
        toast.error("Couldn't load tests");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Selected tests sorted oldest -> newest. If none provided in URL, use all.
  const selectedTests = useMemo(() => {
    const set = new Set(requestedIds);
    const filtered = (set.size === 0 ? allTests : allTests.filter((t) => set.has(t.id))).slice();
    filtered.sort((a, b) => (a.test_date || "").localeCompare(b.test_date || ""));
    return filtered;
  }, [allTests, requestedIds]);

  const displayUnit = useMemo(() => {
    if (selectedTests.length === 0) return "mph";
    // honour the newest test's unit
    return selectedTests[selectedTests.length - 1].speed_unit || "mph";
  }, [selectedTests]);

  const oldestT = selectedTests[0];
  const newestT = selectedTests[selectedTests.length - 1];

  const lt1Old = projectThreshold(oldestT?.lt1, oldestT?.speed_unit, displayUnit);
  const lt1New = projectThreshold(newestT?.lt1, newestT?.speed_unit, displayUnit);
  const lt2Old = projectThreshold(oldestT?.lt2, oldestT?.speed_unit, displayUnit);
  const lt2New = projectThreshold(newestT?.lt2, newestT?.speed_unit, displayUnit);

  const dateRange =
    selectedTests.length >= 2
      ? `${formatLongDate(oldestT.test_date)} → ${formatLongDate(newestT.test_date)}`
      : oldestT
        ? formatLongDate(oldestT.test_date)
        : "";

  const exportPDF = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await buildPdf();
      // Trigger download by re-using jsPDF.save? blob doesn't auto-download.
      // We rebuild here to keep the original download path identical.
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const buildPdf = async () => {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = 210;
    const pageHeight = 297;
    const node = pageRef.current;
    const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
    const img = canvas.toDataURL("image/png");
    const ratio = canvas.width / canvas.height;
    let w = pageWidth;
    let h = w / ratio;
    if (h > pageHeight) {
      h = pageHeight;
      w = h * ratio;
    }
    const x = (pageWidth - w) / 2;
    const y = (pageHeight - h) / 2;
    pdf.addImage(img, "PNG", x, y, w, h, undefined, "FAST");
    const filename = `lactate-progress-${(athlete?.name || "athlete").replace(/\s+/g, "_")}-${
      newestT?.test_date || ""
    }.pdf`;
    return { blob: pdf.output("blob"), filename };
  };

  // Auto-open email modal when navigated with ?send=1 (and data is ready)
  useEffect(() => {
    if (!loading && athlete && selectedTests.length >= 2 && params.get("send") === "1") {
      setEmailOpen(true);
    }
  }, [loading, athlete, selectedTests.length, params]);

  const closeEmail = () => {
    setEmailOpen(false);
    if (params.get("send")) {
      params.delete("send");
      setParams(params, { replace: true });
    }
  };

  if (loading || !athlete) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  if (selectedTests.length < 2) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="font-serif-display text-2xl text-[var(--tir-ink)] mb-2">
          Need at least 2 tests
        </div>
        <p className="text-sm text-[var(--tir-muted)] mb-6">
          A progress report compares the change between two or more tests for {athlete.name}.
        </p>
        <Link
          to={`/athletes/${id}/compare`}
          className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> Back to compare
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="compare-report-page" className="min-h-screen pb-20">
      {/* Toolbar (not printed) */}
      <div className="no-print sticky top-0 z-40 bg-[var(--tir-ivory)]/95 backdrop-blur border-b border-[var(--tir-border)]">
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link
            to={`/athletes/${id}/compare`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
            data-testid="back-to-compare"
          >
            <ArrowLeft size={14} /> Back to compare
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  toast.success("Report link copied");
                } catch {
                  toast.error("Couldn't copy. Long-press the URL bar instead");
                }
              }}
              data-testid="copy-link-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Link2 size={14} /> Copy link
            </button>
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Printer size={14} /> Print
            </button>
            <button
              onClick={exportPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-50"
            >
              <FileDown size={14} /> {exporting ? "Exporting…" : "Download"}
            </button>
            <button
              onClick={() => setEmailOpen(true)}
              data-testid="email-report-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <Mail size={14} /> Send to athlete
            </button>
          </div>
        </div>
      </div>

      <EmailReportModal
        open={emailOpen}
        onClose={closeEmail}
        athlete={athlete}
        testId={null}
        compareTestIds={selectedTests.map((t) => t.id)}
        testDate={newestT?.test_date || ""}
        defaultFilename={`lactate-progress-${(athlete?.name || "athlete").replace(/\s+/g, "_")}-${newestT?.test_date || ""}.pdf`}
        generatePdf={async () => (await buildPdf()).blob}
        reportTypeLabel="compare"
      />

      <div className="pt-8 px-4">
        <div ref={pageRef} className="print-page" data-testid="compare-report-page-1">
          <BrandBanner
            athleteName={athlete.name}
            dateRange={dateRange}
            headline="Lactate Progress Report"
          />
          <div className="px-10 pt-8 pb-20">
            <div className="grid grid-cols-3 gap-6 border-b border-[var(--tir-border)] pb-4 mb-5">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  Tests compared
                </div>
                <div className="font-mono-tir text-sm mt-1">{selectedTests.length}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  Date range
                </div>
                <div className="font-mono-tir text-sm mt-1">{dateRange}</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
                Progress overlay
              </div>
              <div className="font-serif-display text-2xl text-[var(--tir-ink)] mb-3 leading-tight">
                Your lactate curve, then and now
              </div>
              <div className="bg-white">
                <CompareChart tests={selectedTests} displayUnit={displayUnit} height={340} />
              </div>
              <p className="text-[11px] text-[var(--tir-muted)] mt-2 leading-relaxed">
                Older tests are dashed; the most recent test is solid. A leftward shift means more
                lactate at the same speeds; a rightward shift means you can run faster before
                lactate spikes.
              </p>
            </div>

            <div className="mt-7">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
                Threshold changes
              </div>
              <div className="font-serif-display text-2xl text-[var(--tir-ink)] mb-4 leading-tight">
                LT1 and LT2 across your tests
              </div>
              <div className="grid grid-cols-2 gap-5">
                <ChangeCard
                  title="LT1"
                  subtitle="Aerobic Threshold"
                  accent="#1C1917"
                  oldest={lt1Old}
                  newest={lt1New}
                  displayUnit={displayUnit}
                />
                <ChangeCard
                  title="LT2"
                  subtitle="Anaerobic Threshold"
                  accent="#7A1F2B"
                  oldest={lt2Old}
                  newest={lt2New}
                  displayUnit={displayUnit}
                />
              </div>
            </div>

            <div className="mt-6 border-t border-[var(--tir-border)] pt-3">
              <p className="text-[11px] text-[var(--tir-muted)] leading-relaxed">
                Pace and speed are normalised to {displayUnit.toUpperCase()} for comparison
                regardless of each test's original treadmill setting.
              </p>
            </div>
          </div>
          <BrandFooter />
        </div>
      </div>
    </div>
  );
}
